import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Timer,
  Plus,
  Play,
  Pause,
  RotateCcw,
  X,
  Volume2,
  VolumeX,
  Bell,
  ChefHat,
  Flame,
  Snowflake
} from 'lucide-react'

// ══════════════════════════════════════════════════════════════════════════════
// KITCHEN TIMERS - Multi-Timer Manager for Commercial Kitchens
// ══════════════════════════════════════════════════════════════════════════════
//
// Innovative feature: Multiple named timers for tracking different prep tasks
// simultaneously. Perfect for commercial kitchen workflows where you're managing
// braising, marinating, resting, proofing, and more all at once.
//

// Preset timer templates for common kitchen tasks
const TIMER_PRESETS = [
  { name: 'Marinate Chicken', minutes: 30, icon: 'protein', color: 'red' },
  { name: 'Rest Beef', minutes: 10, icon: 'protein', color: 'red' },
  { name: 'Braise Protein', minutes: 120, icon: 'flame', color: 'orange' },
  { name: 'Simmer Sauce', minutes: 45, icon: 'flame', color: 'orange' },
  { name: 'Blanch Vegetables', minutes: 3, icon: 'ice', color: 'blue' },
  { name: 'Ice Bath', minutes: 5, icon: 'ice', color: 'blue' },
  { name: 'Rice Cooking', minutes: 18, icon: 'chef', color: 'amber' },
  { name: 'Proof Dough', minutes: 60, icon: 'chef', color: 'amber' },
  { name: 'Cool Down', minutes: 15, icon: 'ice', color: 'blue' },
  { name: 'Infuse Oil', minutes: 20, icon: 'flame', color: 'green' },
]

function formatTime(seconds) {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function TimerCard({ timer, onToggle, onReset, onDelete }) {
  const progress = timer.totalSeconds > 0
    ? ((timer.totalSeconds - timer.remaining) / timer.totalSeconds) * 100
    : 0

  const isComplete = timer.remaining <= 0
  const isUrgent = timer.remaining > 0 && timer.remaining <= 30

  const colorClasses = {
    red: 'from-red-500 to-red-600',
    orange: 'from-orange-500 to-orange-600',
    blue: 'from-blue-500 to-blue-600',
    amber: 'from-amber-500 to-amber-600',
    green: 'from-emerald-500 to-emerald-600',
    purple: 'from-purple-500 to-purple-600',
  }

  const IconComponent = {
    protein: Flame,
    flame: Flame,
    ice: Snowflake,
    chef: ChefHat,
  }[timer.icon] || Timer

  return (
    <div className={`relative bg-white rounded-xl border overflow-hidden transition-all
      ${isComplete ? 'border-emerald-400 shadow-lg shadow-emerald-100 animate-pulse' :
        isUrgent ? 'border-amber-400 shadow-lg shadow-amber-100' :
        'border-havn-cream-300'}`}
    >
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-havn-cream-200">
        <div
          className={`h-full bg-gradient-to-r ${colorClasses[timer.color] || colorClasses.blue} transition-all duration-500`}
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="p-4 pt-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClasses[timer.color] || colorClasses.blue} text-white flex items-center justify-center`}>
              <IconComponent className="w-4 h-4" />
            </div>
            <div>
              <h4 className="font-semibold text-havn-charcoal text-sm">{timer.name}</h4>
              <p className="text-xs text-havn-gray-400">
                {timer.totalSeconds > 0 ? formatTime(timer.totalSeconds) + ' total' : 'Custom'}
              </p>
            </div>
          </div>
          <button
            onClick={() => onDelete(timer.id)}
            className="p-1 text-havn-gray-300 hover:text-red-500 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Time Display */}
        <div className={`text-center py-3 rounded-lg mb-3
          ${isComplete ? 'bg-emerald-50' : isUrgent ? 'bg-amber-50' : 'bg-havn-cream-50'}`}>
          <span className={`font-mono text-3xl font-bold
            ${isComplete ? 'text-emerald-600' : isUrgent ? 'text-amber-600' : 'text-havn-charcoal'}`}>
            {isComplete ? 'DONE!' : formatTime(timer.remaining)}
          </span>
        </div>

        {/* Controls */}
        <div className="flex gap-2">
          <button
            onClick={() => onToggle(timer.id)}
            className={`flex-1 py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-1.5 transition-colors
              ${timer.isRunning
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                : 'bg-havn-green-700 text-white hover:bg-havn-green-600'}`}
          >
            {timer.isRunning ? (
              <>
                <Pause className="w-4 h-4" />
                Pause
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                {isComplete ? 'Restart' : 'Start'}
              </>
            )}
          </button>
          <button
            onClick={() => onReset(timer.id)}
            className="px-3 py-2 bg-havn-cream-100 text-havn-gray-600 rounded-lg hover:bg-havn-cream-200 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

function AddTimerModal({ onAdd, onClose }) {
  const [name, setName] = useState('')
  const [hours, setHours] = useState(0)
  const [minutes, setMinutes] = useState(5)
  const [seconds, setSeconds] = useState(0)
  const [color, setColor] = useState('blue')

  const handlePreset = (preset) => {
    setName(preset.name)
    setMinutes(preset.minutes)
    setHours(Math.floor(preset.minutes / 60))
    setColor(preset.color)
    // Auto-add the preset
    const totalSeconds = preset.minutes * 60
    onAdd({
      name: preset.name,
      totalSeconds,
      remaining: totalSeconds,
      icon: preset.icon,
      color: preset.color,
    })
  }

  const handleCustomAdd = () => {
    if (!name.trim()) return
    const totalSeconds = (hours * 3600) + (minutes * 60) + seconds
    if (totalSeconds <= 0) return

    onAdd({
      name: name.trim(),
      totalSeconds,
      remaining: totalSeconds,
      icon: 'timer',
      color,
    })
  }

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden animate-slide-up">
        <div className="px-6 py-4 border-b border-havn-cream-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-havn-green-900">Add Timer</h3>
          <button onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Presets */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-havn-charcoal mb-3">Quick Presets</h4>
            <div className="grid grid-cols-2 gap-2">
              {TIMER_PRESETS.slice(0, 6).map((preset, i) => (
                <button
                  key={i}
                  onClick={() => handlePreset(preset)}
                  className="p-3 text-left bg-havn-cream-50 rounded-lg hover:bg-havn-cream-100 transition-colors"
                >
                  <span className="font-medium text-sm text-havn-charcoal block">{preset.name}</span>
                  <span className="text-xs text-havn-gray-400">{preset.minutes} min</span>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-havn-cream-200 pt-6">
            <h4 className="text-sm font-medium text-havn-charcoal mb-3">Custom Timer</h4>

            {/* Timer Name */}
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Timer name (e.g., Braise Short Ribs)"
              className="input mb-4"
            />

            {/* Time Inputs */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <label className="text-xs text-havn-gray-500 block mb-1">Hours</label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  value={hours}
                  onChange={(e) => setHours(parseInt(e.target.value) || 0)}
                  className="input text-center"
                />
              </div>
              <div>
                <label className="text-xs text-havn-gray-500 block mb-1">Minutes</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={minutes}
                  onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
                  className="input text-center"
                />
              </div>
              <div>
                <label className="text-xs text-havn-gray-500 block mb-1">Seconds</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={seconds}
                  onChange={(e) => setSeconds(parseInt(e.target.value) || 0)}
                  className="input text-center"
                />
              </div>
            </div>

            {/* Color Picker */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-havn-gray-500">Color:</span>
              {['red', 'orange', 'amber', 'green', 'blue', 'purple'].map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-6 h-6 rounded-full transition-all ${
                    color === c ? 'ring-2 ring-offset-2 ring-havn-green-700' : ''
                  }`}
                  style={{
                    background: {
                      red: '#ef4444',
                      orange: '#f97316',
                      amber: '#f59e0b',
                      green: '#10b981',
                      blue: '#3b82f6',
                      purple: '#8b5cf6',
                    }[c]
                  }}
                />
              ))}
            </div>

            <button
              onClick={handleCustomAdd}
              disabled={!name.trim() || (hours === 0 && minutes === 0 && seconds === 0)}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Timer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Load timers from localStorage
function loadTimers() {
  try {
    const saved = localStorage.getItem('havn-kitchen-timers')
    if (saved) {
      const parsed = JSON.parse(saved)
      // Recalculate remaining time based on saved timestamp
      const now = Date.now()
      return parsed.map(timer => {
        if (timer.isRunning && timer.savedAt) {
          const elapsed = Math.floor((now - timer.savedAt) / 1000)
          const newRemaining = Math.max(0, timer.remaining - elapsed)
          return { ...timer, remaining: newRemaining }
        }
        return timer
      })
    }
  } catch (e) {
    console.error('Failed to load timers:', e)
  }
  return []
}

export default function KitchenTimers({ onClose }) {
  const [timers, setTimers] = useState(loadTimers)
  const [showAddModal, setShowAddModal] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(true)
  const audioRef = useRef(null)

  // Save timers to localStorage whenever they change
  useEffect(() => {
    const timersToSave = timers.map(t => ({
      ...t,
      savedAt: t.isRunning ? Date.now() : null
    }))
    localStorage.setItem('havn-kitchen-timers', JSON.stringify(timersToSave))
  }, [timers])

  // Tick all running timers every second
  useEffect(() => {
    const interval = setInterval(() => {
      setTimers(prev => prev.map(timer => {
        if (timer.isRunning && timer.remaining > 0) {
          const newRemaining = timer.remaining - 1

          // Play sound and show notification when timer completes
          if (newRemaining === 0) {
            playAlarm(timer.name)
          }

          return { ...timer, remaining: newRemaining }
        }
        return timer
      }))
    }, 1000)

    return () => clearInterval(interval)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  // Note: playAlarm is intentionally excluded to prevent interval restart on sound toggle

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // Play alarm sound and show notification
  const playAlarm = useCallback((timerName = 'Timer') => {
    // Show browser notification (works even when tab is not active)
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('⏰ Kitchen Timer Complete!', {
        body: `${timerName} is done!`,
        icon: '/favicon.ico',
        tag: 'kitchen-timer',
        requireInteraction: true
      })
    }

    if (!soundEnabled) return

    // Use Web Audio API for alarm
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.value = 880 // A5 note
      oscillator.type = 'sine'
      gainNode.gain.value = 0.3

      oscillator.start()

      // Beep pattern
      setTimeout(() => {
        oscillator.frequency.value = 0
        setTimeout(() => {
          oscillator.frequency.value = 880
          setTimeout(() => {
            oscillator.frequency.value = 0
            setTimeout(() => {
              oscillator.frequency.value = 880
              setTimeout(() => {
                oscillator.stop()
                audioContext.close()
              }, 200)
            }, 100)
          }, 200)
        }, 100)
      }, 200)
    } catch (e) {
      console.log('Audio not available')
    }
  }, [soundEnabled])

  // Add new timer
  const handleAddTimer = useCallback((timerData) => {
    setTimers(prev => [...prev, {
      id: Date.now(),
      ...timerData,
      isRunning: false,
    }])
    setShowAddModal(false)
  }, [])

  // Toggle timer running state
  const handleToggle = useCallback((id) => {
    setTimers(prev => prev.map(timer => {
      if (timer.id === id) {
        // If complete, restart
        if (timer.remaining <= 0) {
          return { ...timer, remaining: timer.totalSeconds, isRunning: true }
        }
        return { ...timer, isRunning: !timer.isRunning }
      }
      return timer
    }))
  }, [])

  // Reset timer
  const handleReset = useCallback((id) => {
    setTimers(prev => prev.map(timer =>
      timer.id === id
        ? { ...timer, remaining: timer.totalSeconds, isRunning: false }
        : timer
    ))
  }, [])

  // Delete timer
  const handleDelete = useCallback((id) => {
    setTimers(prev => prev.filter(timer => timer.id !== id))
  }, [])

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-4">
      <div className="bg-havn-cream-100 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden animate-slide-up shadow-elevated flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-havn-green-800 to-havn-green-700 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Timer className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">Kitchen Timers</h3>
              <p className="text-sm text-white/70">{timers.length} active timers</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title={soundEnabled ? 'Mute alarms' : 'Enable alarms'}
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Timers Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {timers.length === 0 ? (
            <div className="text-center py-12">
              <Timer className="w-16 h-16 text-havn-gray-300 mx-auto mb-4" />
              <h4 className="font-display text-lg font-semibold text-havn-charcoal mb-2">
                No Timers Running
              </h4>
              <p className="text-havn-gray-500 mb-4">
                Add timers to track multiple prep tasks at once
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Timer
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {timers.map(timer => (
                <TimerCard
                  key={timer.id}
                  timer={timer}
                  onToggle={handleToggle}
                  onReset={handleReset}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {timers.length > 0 && (
          <div className="px-4 py-3 border-t border-havn-cream-300 bg-white flex-shrink-0">
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Timer
            </button>
          </div>
        )}

        {/* Add Timer Modal */}
        {showAddModal && (
          <AddTimerModal
            onAdd={handleAddTimer}
            onClose={() => setShowAddModal(false)}
          />
        )}
      </div>
    </div>
  )
}
